import { accessibleSignerColor } from "utils/SignerColors";
import {
  FileField,
  RequestParticipant,
  RequestFile,
  SignerAttachment,
  SignRequest,
} from "models/interfaces";
import {
  fetchDimensions,
  fetchInitialsImage,
  fetchMergedSignedDoc,
  fetchMergedSignedPdfAsZip,
  fetchMergedSignedPdfAsZipCallback,
  fetchSignedPdf,
  fetchSignImage,
  fetchSignRequest,
  getDocumentUrl,
  sendOtpRequest,
  verifySecurityCodeRequest,
  verifyOtpRequest,
  fetchSelfSignSignImage,
  fetchSelfSignInitialsImage,
  sendPhoneOtpRequest,
  verifyPhoneOtpRequest,
} from "../SignRequestService";
import {
  callbackQuery,
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  sanitizeSignerEmail,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import {
  getNoAuthCallParams,
  getNoAuthFileCallParams,
  makeCall,
  makeCallFile,
} from "utils/service";
import { setAttachmentPolicy } from "./components/AttachmentPolicy";
import { getDocumentDimensions } from "Services/CommonServices";
import { signerFieldValidationPatterns } from "global/constants/ConstantObject";
import { globalEmitter } from "utils/emitter";
import { needsSignerIdv } from "helpers/identityVerificationHelpers";
import {
  IdvMethod,
  IdvSession,
  IdvTestOutcome,
  SignerIdvState,
} from "types/identityVerification";

// Emitted by field components when a participant finishes editing a field
// (text blur with changed value, checkbox toggle, signature complete, etc.).
// Listened to by ParticipantDesktopScreen / ParticipantMobileScreen which
// batch-save every N commits via saveFieldData. Event-driven (no timers) to
// avoid the dirty-read/write races we hit with debounced saves.
export const FIELD_COMMIT_EVENT = "signRequestFieldCommit";

export const emitFieldCommit = () => {
  globalEmitter.emit(FIELD_COMMIT_EVENT);
};

const attachmentCallbackSuffix = callbackQuery;

/** Uploads one file against one attachment field. Returns the saved record, including its server id. */
export const uploadSignerAttachment = async (
  token: string,
  fieldName: string,
  file: File,
): Promise<SignerAttachment> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fieldName", fieldName);
  const url = `${urls.UPLOAD_SIGNER_ATTACHMENT}/${token}${attachmentCallbackSuffix(token)}`;
  const callParams = await getNoAuthFileCallParams(formData);
  return await makeCall(url, callParams);
};

/** Every file THIS signer has uploaded on this envelope, across all their attachment fields. */
export const fetchSignerAttachments = async (
  token: string,
): Promise<SignerAttachment[]> => {
  const url = `${urls.FETCH_SIGNER_ATTACHMENTS}/${token}${attachmentCallbackSuffix(token)}`;
  const callParams = getNoAuthCallParams("GET");
  const response = await makeCall(url, callParams);
  return Array.isArray(response) ? response : [];
};

let attachmentsRequest: {
  token: string;
  promise: Promise<SignerAttachment[]>;
} | null = null;

export const loadSignerAttachments = (
  token: string,
): Promise<SignerAttachment[]> => {
  if (!attachmentsRequest || attachmentsRequest.token !== token) {
    attachmentsRequest = {
      token,
      // A failure must not be cached, or every field that mounts afterwards inherits the rejection
      // and the signer can never recover without a reload.
      promise: fetchSignerAttachments(token).catch((error) => {
        attachmentsRequest = null;
        throw error;
      }),
    };
  }
  return attachmentsRequest.promise;
};

/** Drops the shared fetch so the next read hits the server. Call after any upload or delete. */
export const invalidateSignerAttachments = () => {
  attachmentsRequest = null;
};

/** Removes one of this signer's own uploads. The server refuses once they have signed. */
export const deleteSignerAttachment = async (
  token: string,
  attachmentId: string,
) => {
  const url = `${urls.SIGNER_ATTACHMENT}/${attachmentId}/request/${token}${attachmentCallbackSuffix(token)}`;
  const callParams = getNoAuthCallParams("DELETE");
  return await makeCall(url, callParams);
};

/** Downloads one of this signer's own uploads — used to preview a file after a page reload. */
export const downloadSignerAttachment = async (
  token: string,
  attachmentId: string,
): Promise<Blob> => {
  const url = `${urls.SIGNER_ATTACHMENT}/${attachmentId}/download/request/${token}${attachmentCallbackSuffix(token)}`;
  const callParams = getNoAuthCallParams("GET");
  return await makeCallFile(url, callParams);
};

export const attachmentErrorMessage = (error: any, fallback: string): string => {
  const message = error?.message ?? error?.error?.message;
  return typeof message === "string" && message.trim() && message !== "Something went wrong."
    ? message
    : fallback;
};

export const fetchPdfSignRequest = async (
  reqToken: string,
  selectedFile: RequestFile,
  setSelectedFile: Function,
  setLoading: Function,
  setFileSrc: Function,
  dispatch: Function,
  setReqIsVoided: Function,
  fetchPdfRequester: Function,
  errorHandler?: Function,
  setAttachSingleCertificate?: Function,
  setAllowDelegation?: Function,
) => {
  try {
    setLoading(true);
    let signRequest: SignRequest = await fetchSignRequest(reqToken);
    // The signer is a guest and cannot read the account-config endpoint, so the backend echoes the
    // attachment limits on the envelope. Cache them before any attachment field renders. Absent on
    // envelopes with no attachment fields, and on older backends — the setter ignores a null.
    setAttachmentPolicy((signRequest as any)?.attachmentPolicy);
    // A fresh envelope read means any cached attachment list belongs to the previous one.
    invalidateSignerAttachments();
    let fieldsIdCounter = 1;
    if (signRequest.signers?.length) {
      signRequest.signers = signRequest.signers.map((s: any) => ({
        ...s,
        email:
          sanitizeSignerEmail(s.email) ||
          (typeof s.email === "string" ? s.email : "") ||
          "",
      }));
    }
    if (signRequest.approval?.reviewers?.length) {
      signRequest.approval.reviewers = signRequest.approval.reviewers.map(
        (r: any) => ({
          ...r,
          email:
            sanitizeSignerEmail(r.email) ||
            (typeof r.email === "string" ? r.email : "") ||
            "",
        }),
      );
    }
    setAllowDelegation &&
      setAllowDelegation(
        signRequest.signers?.[0]?.delegated === true
          ? false
          : Boolean(signRequest.signRequestConfig?.allowSignatureDelegation),
      );
    signRequest.files.forEach((file: RequestFile) => {
      file.unsignedDoc = (file.unsignedPdf || file.unsignedDoc || "").substring(
        file.unsignedDoc?.indexOf("/") + 1,
      );
      file.fields.forEach((field: FileField) => {
        field.id = fieldsIdCounter;
        fieldsIdCounter += 1;
        if (field.type === "date_signed" && !isTruthy(field.value)) {
          const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          };
          field.value = new Date().toLocaleDateString("en-US", options);
        } else if (field.type === strings.email) {
          const raw = signRequest.signers?.[0]?.email;
          field.value =
            sanitizeSignerEmail(raw) ||
            (typeof raw === "string" ? raw : "") ||
            "";
        }
      });
    });
    signRequest.approval = signRequest.approval || {
      approvalNeeded: false,
      orderNeeded: false,
      reviewers: [],
    };

    dispatch({
      type: "SET_PDF_SIGN_REQUEST",
      signRequest: signRequest,
    });
    // Always re-fetch the current participant's record (name/email/status and,
    // crucially, requestSecurity) for THIS request token. Previously this only
    // ran when an approval workflow was configured, so selectedParticipant —
    // persisted in redux/localStorage — kept whatever was left over from a
    // different request (e.g. its requestSecurity), and a plain signing
    // request with no security configured could incorrectly show the
    // security-code gate.
    fetchPdfRequester(signRequest.approval?.reviewers);
    // Until the signer passes identity verification every document call
    // answers 403 IDV_REQUIRED, so don't ask — the verification screen shows
    // instead, and the next fetch (after passing) loads the document.
    if (
      isTruthy(signRequest.signers) &&
      !needsSignerIdv(signRequest.signerIdentityVerification)
    ) {
      if (signRequest.signers[0].status === strings.pending) {
        await getUnsignedFile(
          signRequest.files[0],
          reqToken,
          selectedFile,
          setSelectedFile,
        );
      } else {
        getSignedFile(
          signRequest.files[0],
          reqToken,
          selectedFile,
          setLoading,
          setFileSrc,
          setSelectedFile,
        );
      }
    }

    setAttachSingleCertificate &&
      setAttachSingleCertificate(
        signRequest.signRequestConfig.attachSingleCertificate,
      );
  } catch (error: any) {
    setLoading(false);
    const backendMessage = (error?.message || "").toString();
    const normalizedBackendMessage = backendMessage.toLocaleLowerCase();
    const isNoLongerAssociatedError = normalizedBackendMessage.includes(
      strings.nonAssociatedErrorMessage.toLocaleLowerCase(),
    );

    if (errorHandler) {
      if (error.code === 406 || isNoLongerAssociatedError) {
        const status = errorHandler(backendMessage);
        if (
          status === strings.voided ||
          status === strings.expired ||
          status === strings.nonAssociatedErrorMessage ||
          status === strings.notYourTurnErrorMessageV1 ||
          status === strings.notYourTurnErrorMessageV2 ||
          status === strings.requestDelegatedMessage
        ) {
          return;
        }
      } else {
        handleErrorMessage(error);
      }
    } else {
      handleErrorMessage(error);
    }

    throw error;
  } finally {
    setLoading(false);
  }
};

export const getUnsignedFile = async (
  file: RequestFile,
  reqToken: string,
  selectedFile: RequestFile,
  setSelectedFile: Function,
  setLoading?: Function,
) => {
  let requestFile = {
    ...file,
    pageUrls: [],
    unsignedPdfUrl: undefined,
    unsignedDocUrl: undefined,
  };

  try {
    setLoading && setLoading(true);
    const pageCount = file.pageCount;
    const count = pageCount <= 1 ? pageCount : 1;

    for (let pageIndex = 0; pageIndex < count; pageIndex++) {
      const dimension = await fetchDimensions(
        { unsignedPdf: requestFile.unsignedDoc },
        reqToken!,
        pageIndex,
      );
      const response = await getDocumentUrl(requestFile, true, reqToken, true);

      requestFile.unsignedPdfUrl = response?.message;
      requestFile.dimensions = {
        ...requestFile.dimensions,
        ...dimension[requestFile.unsignedDoc],
      };
    }
    setSelectedFile(requestFile);
    setLoading && setLoading(false);
    return requestFile;
  } catch (error: any) {
    setLoading && setLoading(false);
    handleErrorMessage(error);
  }
};

export const getUnsignedDocument = async (
  file: RequestFile,
  reqToken: string,
  selectedFile: RequestFile,
  setSelectedFile: Function,
  setLoading?: Function,
) => {
  let requestFile = {
    ...file,
    unsignedPdfUrl: undefined,
    unsignedDocUrl: undefined,
  };

  try {
    setLoading && setLoading(true);
    const dimension = await getDocumentDimensions(
      { unsignedPdf: requestFile.unsignedDoc || requestFile.unsignedPdf },
      true,
      reqToken!,
      true,
    );
    const url = await getDocumentUrl(requestFile, true, reqToken, true);
    requestFile.unsignedDocUrl = url.message!;
    requestFile.dimensions = {
      ...dimension,
    };
    setLoading && setLoading(false);

    setSelectedFile(requestFile);
    setLoading && setLoading(false);
    return requestFile;
  } catch (error: any) {
    setLoading && setLoading(false);
    handleErrorMessage(error);
  }
};

export const getSignedFile = async (
  file: RequestFile,
  reqToken: string,
  selectedFile: RequestFile,
  setLoading: Function,
  setFileSrc: Function,
  setSelectedFile: Function,
) => {
  let requestFile = { ...selectedFile, ...file };
  try {
    setLoading(true);
    const res: any = await fetchSignedPdf(reqToken, file);
    const url = URL.createObjectURL(res);
    setFileSrc(url);
    setSelectedFile(requestFile);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const getSignatureImage = async (
  reqToken: string,
  setSignatureExist: Function,
  dispatch: Function,
  setIsOpen: Function,
) => {
  try {
    const res = await fetchSignImage(reqToken);
    const reader = new FileReader();
    reader.readAsDataURL(res);
    reader.onloadend = () => {
      const url: any = reader.result;
      if (isTruthy(url.split(",")[1])) {
        dispatch({
          type: "UPDATE_SIGN_IMAGE",
          signImage: url,
        });
        setSignatureExist(true);
      } else {
        setIsOpen(true);
      }
    };
  } catch (error: any) {
    setIsOpen(true);
    handleErrorMessage(error);
  }
};

export const getSelfSignSignatureImage = async (
  setSignatureExist: Function,
  dispatch: Function,
  setIsOpen: Function,
) => {
  try {
    const res = await fetchSelfSignSignImage();
    const reader = new FileReader();
    reader.readAsDataURL(res);
    reader.onloadend = () => {
      const url: any = reader.result;
      if (isTruthy(url.split(",")[1])) {
        dispatch({
          type: "UPDATE_SIGN_IMAGE",
          signImage: url,
        });
        setSignatureExist(true);
      } else {
        setIsOpen(true);
      }
    };
  } catch (error: any) {
    setIsOpen(true);
    handleErrorMessage(error);
  }
};

export const getSelfSignInitialsImage = async (
  setSignatureExist: Function,
  dispatch: Function,
  setIsOpen: Function,
) => {
  try {
    const res = await fetchSelfSignInitialsImage();
    const reader = new FileReader();
    reader.readAsDataURL(res);
    reader.onloadend = () => {
      const url: any = reader.result;
      if (isTruthy(url.split(",")[1])) {
        dispatch({
          type: "UPDATE_INITIALS_IMAGE",
          initialsImage: url,
        });
        setSignatureExist(true);
      } else {
        setIsOpen(true);
      }
    };
  } catch (error: any) {
    setIsOpen(true);
    handleErrorMessage(error);
  }
};

export const getInitialsImage = async (
  reqToken: string,
  setSignatureExist: Function,
  dispatch: Function,
  setIsOpen: Function,
) => {
  try {
    const res = await fetchInitialsImage(reqToken);
    const reader = new FileReader();
    reader.readAsDataURL(res);
    reader.onloadend = () => {
      const url: any = reader.result;
      if (isTruthy(url.split(",")[1])) {
        dispatch({
          type: "UPDATE_INITIALS_IMAGE",
          initialsImage: url,
        });
        setSignatureExist(true);
      } else {
        setIsOpen(true);
      }
    };
  } catch (error: any) {
    setIsOpen(true);
    handleErrorMessage(error);
  }
};

export const areAllFieldsFilled = (
  signRequest: SignRequest,
  setAllFieldsFilled: Function,
  setAllRequiredFieldsFilled: Function,
): number[] => {
  let fieldIds = [];
  let allRequiredFieldsFilled = true;
  let allFieldsFilled = true;

  for (const file of signRequest.files) {
    const radiosByGroup = new Map<string, FileField[]>();
    for (const field of file.fields) {
      if (field.type === "radio_button") {
        if (field.groupId) {
          const arr = radiosByGroup.get(field.groupId) ?? [];
          arr.push(field);
          radiosByGroup.set(field.groupId, arr);
        }
        continue;
      }
      if (
        field.required &&
        (!field.value ||
          (typeof field.value === "string" && field.value.trim() === ""))
      ) {
        fieldIds.push(field.id);
        allRequiredFieldsFilled = false;
      }
      if (
        (!field.value || field.value === "") &&
        (field.type === "signature" || field.type === "initials")
      ) {
        allFieldsFilled = false;
        fieldIds.push(field.id);
      }
      if (!validateField(field)) {
        allFieldsFilled = false;
      }
    }
    radiosByGroup.forEach((siblings) => {
      const anyRequired = siblings.some((f) => f.required);
      if (!anyRequired) return;
      const anySelected = siblings.some((f) => f.value === "true");
      if (!anySelected) {
        allRequiredFieldsFilled = false;
        siblings.forEach((f) => fieldIds.push(f.id));
      }
    });
  }

  setAllRequiredFieldsFilled(allRequiredFieldsFilled);
  setAllFieldsFilled(allFieldsFilled);

  return fieldIds;
};

export const downloadCombinedFileAsZip = async (
  requestToken: string,
  signRequest: SignRequest,
  setLoading: Function,
) => {
  try {
    setLoading(true);
    const res = await fetchMergedSignedPdfAsZipCallback(
      requestToken,
      signRequest,
    );
    const url = URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${signRequest.emailSubject}.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const downloadFilesAsZip = async (
  signRequest: SignRequest,
  setLoading: Function,
) => {
  // Callers pass the fetch/request response straight through; it comes back
  // empty when the user can't load the request, and posting that would send
  // the zip endpoint an empty body (a bare 400).
  if (!isTruthy(signRequest)) {
    openErrorNotification(notifiers.REQUEST_DOWNLOAD_UNAVAILABLE);
    setLoading(false);
    return;
  }
  try {
    setLoading(true);
    const res = await fetchMergedSignedPdfAsZip(signRequest);
    const url = URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${signRequest.emailSubject}.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
    setLoading(false);
  } catch (error: any) {
    setLoading(false);
    handleErrorMessage(error);
  }
};

export const downloadCombinedFile = async (
  reqToken: string,
  signRequest: SignRequest,
) => {
  try {
    // setLoading(true);
    const res = await fetchMergedSignedDoc(reqToken, signRequest);
    const url = URL.createObjectURL(res);
    // downloadFile(url);
    // setLoading(false);
  } catch (error: any) {
    // setLoading(false);
    handleErrorMessage(error);
  }
};

export const fieldsRemaining = (files: RequestFile[]) => {
  const result = files.reduce(
    (acc, requestFile) => {
      acc.totalFields += requestFile.fields.length;
      requestFile.fields.forEach((field) => {
        const rawValue = field.value as unknown;
        const hasValue =
          (typeof rawValue === "string" && rawValue.trim() !== "") ||
          rawValue === true ||
          rawValue === "true";

        if (hasValue) {
          acc.fieldsWithValue += 1;
        }

        if (field.required) {
          acc.requiredValue += 1;
        }
        if (hasValue && field.required) {
          acc.requiredWithValue += 1;
        }
      });
      return acc;
    },
    {
      totalFields: 0,
      fieldsWithValue: 0,
      requiredValue: 0,
      requiredWithValue: 0,
    },
  );
  return result;
};

/** A sender-typed pattern that does not compile is treated as "no check". */
const safeRegExp = (pattern: string): RegExp | null => {
  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
};

/** Field types whose value is checked against the sender's rules before signing. */
const VALUE_VALIDATED_FIELD_TYPES: string[] = [
  strings.name,
  strings.email,
  strings.dateSigned,
  strings.text,
  strings.dropDown,
  strings.title,
  strings.companyName,
];

/**
 * True only for a field placed from the custom field library. The custom-field rules apply to
 * these and to nothing else, so a field dragged from the Fields panel keeps exactly the
 * validation it had before this feature. Mirrors PdfField.isCustomField on the server.
 */
export const isLibraryField = (field: FileField): boolean =>
  !!(field as any).customFieldId;

/**
 * The submit-time check a Fields-panel field has always had: the named pattern, applied only when
 * the field is REQUIRED. Kept verbatim so existing fields behave exactly as they did before.
 */
export const legacySubmitPatternError = (
  field: FileField,
  rawValue: string | undefined,
): string => {
  if (field.validationPattern !== "none" && field.required) {
    const pattern =
      signerFieldValidationPatterns[
        field.validationPattern as keyof typeof signerFieldValidationPatterns
      ];
    if (pattern && !pattern.test(rawValue || "")) {
      return field.validationMessage || "Field value is not valid";
    }
  }
  return "";
};

/**
 * The inline check a Fields-panel field has always had, used while the signer types: the named
 * pattern, applied to any NON-EMPTY value regardless of Required. Deliberately different from
 * {@link legacySubmitPatternError} because the original code differed in exactly this way.
 */
export const legacyInlinePatternError = (
  field: FileField,
  rawValue: string | undefined,
): string => {
  const value = rawValue ?? "";
  if (field.validationPattern !== "none" && value !== "") {
    const pattern =
      signerFieldValidationPatterns[
        field.validationPattern as keyof typeof signerFieldValidationPatterns
      ];
    if (pattern && !pattern.test(value)) {
      return field.validationMessage || "Field value is not valid.";
    }
  }
  return "";
};

/**
 * Why a filled value would be refused, or "" when it passes. Mirrors the
 * server's DocumentSignHelper.appendValueErrors so the signer sees the same
 * refusal inline instead of on submit: min length, dropdown
 * membership, then the pattern (a named one, or the sender's own regex when
 * `customValidation` is set). An empty value is left to the required check.
 */
export const customFieldValueError = (
  field: FileField,
  rawValue: string | undefined,
): string => {
  // Types whose value is not text: a checkbox and a radio carry true/false, the signature-style
  // fields carry an image, and an attachment carries file ids. None of the rules below can mean
  // anything for them, and a checkbox's value arrives as a real boolean -- calling .trim() on it
  // threw, which is what surfaced as "Something went wrong" the moment a signer ticked one.
  // Mirrors the early return in the server's DocumentSignHelper.appendValueErrors.
  if (
    field.type === strings.checkbox ||
    field.type === strings.radioButton ||
    field.type === strings.signature ||
    field.type === strings.initials ||
    field.type === strings.attachment
  ) {
    return "";
  }
  const value = String(rawValue ?? "");
  if (value.trim() === "") return "";
  const label = field.label || field.name || "This field";

  if (
    field.type === strings.dropDown &&
    (field.listItems?.length ?? 0) > 0 &&
    !field.listItems!.some((item) => item.text === value)
  ) {
    return `${label} must be one of its options.`;
  }

  // Length applies to a typed value only. A dropdown's value is one of its options, so a length
  // rule could only refuse a choice the signer had no way to avoid.
  if (
    field.type !== strings.dropDown &&
    (field.minLength ?? 0) > 0 &&
    value.length < field.minLength!
  ) {
    return `${label} must be at least ${field.minLength} characters.`;
  }

  if (field.validationPattern && field.validationPattern !== "none") {
    const pattern = field.customValidation
      ? safeRegExp(field.validationPattern)
      : signerFieldValidationPatterns[
          field.validationPattern as keyof typeof signerFieldValidationPatterns
        ];
    if (pattern && !pattern.test(value)) {
      return field.validationMessage || "Field value is not valid.";
    }
  }
  return "";
};

export const validateAndProcessPdfFiles = (requestFiles: RequestFile[]) => {
  for (const requestFile of requestFiles) {
    for (const field of requestFile.fields) {
      // Condition 1: Replace value of "signature" fields with "image"
      if (field.type === "signature" || field.type === "initials") {
        field.value = "image";
      }
      if (!VALUE_VALIDATED_FIELD_TYPES.includes(field.type)) continue;
      // Condition 1.5: Validate maxLength for text-like fields with existing values.
      // This is important for copied requests where field maxLength might be reduced later.
      if (field.type !== strings.dropDown) {
        const fieldValue = field.value || "";
        const maxLength = field.maxLength || 200;
        if (fieldValue.length > maxLength) {
          return {
            allFieldsValid: false,
            invalidField: field,
            invalidReason: `Field value exceeds max length (${maxLength})`,
          };
        }
      }
      // Condition 2: a library field takes the sender's full rules (pattern, length,
      // options) on any filled value; a Fields-panel field keeps the original check.
      const invalidReason = isLibraryField(field)
        ? customFieldValueError(field, field.value)
        : legacySubmitPatternError(field, field.value);
      if (invalidReason) {
        return { allFieldsValid: false, invalidField: field, invalidReason };
      }
    }
  }

  return { allFieldsValid: true, invalidField: undefined, invalidReason: "" }; // Return true if all fields are valid
};

//reducer and types for signer side. Keep all functions above this line.

type Action =
  | { type: "SET_PDF_SIGN_REQUEST"; signRequest: SignRequest }
  | {
      type: "UPDATE_FIELD";
      fileId: string;
      fieldId: number | string;
      value: string;
    }
  | {
      type: "UPDATE_FILE_IN_REQUEST";
      file: RequestFile;
    }
  | {
      type: "UPDATE_SIGN_IMAGE";
      signImage: string;
    }
  | {
      type: "UPDATE_INITIALS_IMAGE";
      initialsImage: string;
    }
  | {
      type: "UPDATE_VALID_FIELDS";
      fieldId: string | number;
      isValid: boolean;
    }
  | {
      type: "UPDATE_COMMON_FIELD";
      fieldType: string;
      value: string;
    }
  | {
      type: "REPLACE_SIGNED_IMAGES";
      fieldType: "signature" | "initials";
      signerIndex: number;
      value: string;
    }
  | {
      type: "UPDATE_RADIO_GROUP";
      fileId: string;
      groupId: string;
      selectedFieldId: number;
    };

export interface SignerState {
  signRequest: SignRequest;
  signImage: string;
  initialsImage: string;
  validFields: { [fieldId: string | number]: boolean };
}

export const initialState: SignerState = {
  signRequest: {
    id: "",
    signers: [],
    files: [],
    emailSubject: "",
    emailMessage: "",
    expirationDays: 180,
    requestedBy: { name: "", email: "" },
    signedBy: "",
    status: "",
    approval: {
      approvalNeeded: false,
      orderNeeded: false,
      reviewers: [],
    },
    reminders: {
      daysBeforeFirstReminder: 5,
      daysBetweenReminders: 3,
    },
    needReminders: false,
    signRequestConfig: {
      attachDocumentToMailOnCompletion: true,
      allowDocumentDownload: true,
      sendNotificationOnCompletion: true,
      attachSingleCertificate: true,
      allowSignatureDelegation: true,
    },
    currentSigners: [],
    requestToken: "",
    autoGeneratedId: 0,
    templateId: null,
  },
  signImage: "",
  initialsImage: "",
  validFields: {},
};

export const reducer = (state: SignerState, action: Action): SignerState => {
  switch (action.type) {
    case "SET_PDF_SIGN_REQUEST":
      return {
        ...state,
        signRequest: action.signRequest,
      };
    case "UPDATE_FILE_IN_REQUEST": {
      const updatedFiles = state.signRequest.files.map((file: any) => {
        if (
          file.unsignedPdf === action.file.unsignedPdf ||
          file.unsignedDoc === action.file.unsignedDoc
        ) {
          // Create a new file object with updated fields
          return {
            ...file,
            fields: action.file.fields,
          };
        }
        // Return a new object reference even if file doesn't match
        return { ...file };
      });
      return {
        ...state,
        signRequest: {
          ...state.signRequest,
          files: updatedFiles,
        },
      };
    }
    case "UPDATE_FIELD": {
      const { fileId, fieldId, value } = action;
      let fileFound = false;

      // Normalize fileId to match how files are stored (filename only, no path)
      const normalizeFileId = (id: string) => {
        if (!id) return "";
        // Extract filename from path if it contains a slash
        const lastSlashIndex = id.lastIndexOf("/");
        if (lastSlashIndex >= 0 && lastSlashIndex < id.length - 1) {
          return id.substring(lastSlashIndex + 1);
        }
        return id;
      };

      const normalizedFileId = normalizeFileId(fileId || "");

      const updatedFiles = state.signRequest.files.map((file: any) => {
        // Match file by unsignedPdf or unsignedDoc (handle both cases)
        // Files in state have unsignedDoc normalized (filename only)
        const fileUnsignedPdf = file.unsignedPdf || "";
        const fileUnsignedDoc = file.unsignedDoc || "";
        const normalizedFilePdf = normalizeFileId(fileUnsignedPdf);
        const normalizedFileDoc = normalizeFileId(fileUnsignedDoc);

        const fileMatches =
          file.unsignedPdf === fileId ||
          file.unsignedDoc === fileId ||
          normalizedFilePdf === normalizedFileId ||
          normalizedFileDoc === normalizedFileId ||
          fileUnsignedPdf === normalizedFileId ||
          fileUnsignedDoc === normalizedFileId;

        if (fileMatches) {
          fileFound = true;
          const updatedFields = file.fields.map((field: any) => {
            if (field.name === fieldId) {
              // Create a new field object with updated value, preserving all other properties
              return { ...field, value };
            }
            // Return a new field object to avoid read-only issues
            return { ...field };
          });
          // Create a completely new file object to avoid read-only property issues
          return {
            ...file,
            fields: updatedFields,
          };
        }
        // Return a new object reference even if file doesn't match
        return { ...file };
      });

      // Log warning if file wasn't found (for debugging)
      if (!fileFound && fileId) {
        console.warn(
          `UPDATE_FIELD: File not found for fileId: ${fileId} (normalized: ${normalizedFileId}), fieldId: ${fieldId}`,
        );
        console.warn(
          "Available files:",
          state.signRequest.files.map((f: any) => ({
            unsignedPdf: f.unsignedPdf,
            unsignedDoc: f.unsignedDoc,
          })),
        );
      }

      return {
        ...state,
        signRequest: {
          ...state.signRequest,
          files: updatedFiles,
        },
      };
    }
    case "UPDATE_RADIO_GROUP": {
      const { fileId, groupId, selectedFieldId } = action;
      const normalizeFileId = (id: string) => {
        if (!id) return "";
        const lastSlashIndex = id.lastIndexOf("/");
        if (lastSlashIndex >= 0 && lastSlashIndex < id.length - 1) {
          return id.substring(lastSlashIndex + 1);
        }
        return id;
      };
      const normalizedFileId = normalizeFileId(fileId || "");
      const updatedFiles = state.signRequest.files.map((file: any) => {
        const fileUnsignedPdf = file.unsignedPdf || "";
        const fileUnsignedDoc = file.unsignedDoc || "";
        const normalizedFilePdf = normalizeFileId(fileUnsignedPdf);
        const normalizedFileDoc = normalizeFileId(fileUnsignedDoc);
        const fileMatches =
          file.unsignedPdf === fileId ||
          file.unsignedDoc === fileId ||
          normalizedFilePdf === normalizedFileId ||
          normalizedFileDoc === normalizedFileId ||
          fileUnsignedPdf === normalizedFileId ||
          fileUnsignedDoc === normalizedFileId;
        if (!fileMatches) return { ...file };
        const updatedFields = file.fields.map((field: any) => {
          if (field.type === "radio_button" && field.groupId === groupId) {
            return {
              ...field,
              value: field.id === selectedFieldId ? "true" : "false",
            };
          }
          return { ...field };
        });
        return { ...file, fields: updatedFields };
      });
      return {
        ...state,
        signRequest: { ...state.signRequest, files: updatedFiles },
      };
    }
    case "UPDATE_COMMON_FIELD": {
      const { fieldType, value } = action;
      const updatedFiles = state.signRequest.files.map((file: any) => {
        const updatedFields = file.fields.map((field: any) => {
          if (field.type === fieldType) {
            // Create a new field object with updated value
            return { ...field, value };
          }
          // Return a new field object to avoid read-only issues
          return { ...field };
        });
        // Create a completely new file object
        return {
          ...file,
          fields: updatedFields,
        };
      });

      return {
        ...state,
        signRequest: {
          ...state.signRequest,
          files: updatedFiles,
        },
      };
    }
    case "REPLACE_SIGNED_IMAGES": {
      // The server keeps ONE signature (and one initials) image per signer and stamps it on every
      // one of their fields of that type, so a newly drawn signature replaces the earlier one on
      // the signed PDF. Mirror that here: every field this signer has already signed takes the
      // new image, so the screen shows what will actually be signed. Empty fields stay empty --
      // the signer still places those themselves.
      const { fieldType, signerIndex, value } = action;
      const updatedFiles = state.signRequest.files.map((file: any) => ({
        ...file,
        fields: file.fields.map((field: any) =>
          field.type === fieldType &&
          field.signerIndex === signerIndex &&
          isTruthy(field.value)
            ? { ...field, value }
            : { ...field },
        ),
      }));
      return {
        ...state,
        signRequest: {
          ...state.signRequest,
          files: updatedFiles,
        },
      };
    }
    case "UPDATE_SIGN_IMAGE": {
      return {
        ...state,
        signImage: action.signImage,
      };
    }
    case "UPDATE_INITIALS_IMAGE": {
      return {
        ...state,
        initialsImage: action.initialsImage,
      };
    }
    case "UPDATE_VALID_FIELDS": {
      return {
        ...state,
        validFields: { ...state.validFields, [action.fieldId]: action.isValid },
      };
    }
    default:
      return state;
  }
};

export const SignerInitialState: SignerState = {
  signRequest: {
    id: "",
    signers: [],
    files: [],
    emailSubject: "",
    emailMessage: "",
    expirationDays: 180,
    requestedBy: { name: "", email: "" },
    signedBy: "",
    status: "",
    approval: {
      approvalNeeded: false,
      orderNeeded: false,
      reviewers: [],
    },
    reminders: {
      daysBeforeFirstReminder: 5,
      daysBetweenReminders: 3,
    },
    needReminders: false,
    signRequestConfig: {
      attachDocumentToMailOnCompletion: true,
      allowDocumentDownload: true,
      sendNotificationOnCompletion: true,
      attachSingleCertificate: true,
      allowSignatureDelegation: true,
    },
    currentSigners: [],
    requestToken: "",
    autoGeneratedId: 0,
    templateId: null,
  },
  signImage: "",
  initialsImage: "",
  validFields: {},
};

export const validateField = (field: FileField) =>
  (isLibraryField(field)
    ? customFieldValueError(field, field.value)
    : legacyInlinePatternError(field, field.value)) === "";

// Verification APIs
export const sendOtp = async (reqToken: string): Promise<any> => {
  return await sendOtpRequest(reqToken);
};

export const verifyOtp = async (
  reqToken: string,
  otp: string,
  participant: RequestParticipant,
): Promise<any> => {
  const sec = participant.requestSecurity;

  const body = {
    name: participant.name,
    email: participant.email,
    order: participant.order ?? 0,
    id: participant.id ?? 0,
    color: accessibleSignerColor(participant.color),
    requestSecurity: {
      needSecuredAccess: sec?.needSecuredAccess ?? true,
      securityCode: sec?.securityCode ?? null,
      needOTPValidation: true,
      otp,
    },
  };

  return await verifyOtpRequest(reqToken, body);
};

// Phone (SMS) OTP — same request/verify shapes as email OTP, mirrored at the
// phone-otp URLs instead. `needPhoneValidation: true` tells the backend which
// method is active (mutually exclusive with `needOTPValidation`).
export const sendPhoneOtp = async (reqToken: string): Promise<any> => {
  return await sendPhoneOtpRequest(reqToken);
};

export const verifyPhoneOtp = async (
  reqToken: string,
  otp: string,
  participant: RequestParticipant,
): Promise<any> => {
  const sec = participant.requestSecurity;

  const body = {
    name: participant.name,
    email: participant.email,
    order: participant.order ?? 0,
    id: participant.id ?? 0,
    color: accessibleSignerColor(participant.color),
    requestSecurity: {
      needSecuredAccess: sec?.needSecuredAccess ?? true,
      securityCode: sec?.securityCode ?? null,
      needOTPValidation: false,
      needPhoneValidation: true,
      otp,
    },
  };

  return await verifyPhoneOtpRequest(reqToken, body);
};

export const verifySecurityCode = async (
  reqToken: string,
  securityCode: string,
  participant: RequestParticipant,
) => {
  const body = {
    name: participant.name,
    email: participant.email,
    order: participant.order ?? 0,
    id: participant.id ?? 0,
    color: accessibleSignerColor(participant.color),
    requestSecurity: {
      needSecuredAccess: true,
      securityCode,
      needOTPValidation: false,
    },
  };
  return await verifySecurityCodeRequest(reqToken, body);
};

// ---------------------------------------------------------------------------
// Signer identity verification. Every call carries the link's ?id=&key= pair,
// exactly like the other callback calls on this screen.
// ---------------------------------------------------------------------------

/** The signer's IDV state, for when it isn't already on the token fetch. */
export const fetchSignerIdentityVerification = async (
  reqToken: string,
): Promise<SignerIdvState> => {
  const url = `${urls.FETCH_SIGNER_IDV}/${reqToken}${callbackQuery(reqToken)}`;
  return await makeCall(url, getNoAuthCallParams("GET"));
};

/** Starts `method`, or the signer's `nextMethod` when omitted. */
export const startIdentityVerification = async (
  reqToken: string,
  method?: IdvMethod | null,
): Promise<IdvSession> => {
  const url = `${urls.START_IDV_SESSION}/${reqToken}${callbackQuery(reqToken)}`;
  return await makeCall(
    url,
    getNoAuthCallParams("POST", method ? { method } : {}),
  );
};

/**
 * Polls a session; answers the same state as the fetch. Without `sessionId`
 * the backend polls the signer's open session (or just returns the state).
 */
export const checkIdentityVerification = async (
  reqToken: string,
  sessionId?: string | null,
): Promise<SignerIdvState> => {
  const url = `${urls.VERIFY_IDV_SESSION}/${reqToken}${callbackQuery(reqToken)}`;
  return await makeCall(
    url,
    getNoAuthCallParams("POST", sessionId ? { sessionId } : {}),
  );
};

/** Test vendor only (dev / UAT): 404 wherever the test vendor isn't configured. */
export const submitIdentityVerificationTestOutcome = async (
  reqToken: string,
  sessionId: string,
  outcome: IdvTestOutcome,
): Promise<SignerIdvState> => {
  const url = `${urls.SUBMIT_IDV_TEST_OUTCOME}/${reqToken}${callbackQuery(reqToken)}`;
  return await makeCall(
    url,
    getNoAuthCallParams("POST", { sessionId, outcome }),
  );
};
